import React, { useState } from "react";
import { Link as RouterLink } from "react-router";

import InputMask from "react-input-mask";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { useMediaQuery } from "@mui/material";

import CallModal from "../Modal";
import Button from "components/Button";

import { useAppDispatch } from "store/hooks";
import { preOrderIban } from "store/features/preOrderSlice";

import { routes } from "routes/routes";

import UAFlag from "assets/images/ua_flag-icon.svg";
import ContactUsBg from "assets/images/contact-us-bg.jpg";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";
import { FbPixelAnalyticsEvents } from "shared/constants/fb-pixel-analytics";

import { grey000, grey900 } from "styles";

const ContactUs: React.FC = () => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);

  const isXs = useMediaQuery("(max-width: 599px)");

  const onChangePhone = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value.length === 18 && setShowTooltip(false);
    setPhoneNumber(event.target.value);
  };

  const onClick = () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setShowTooltip(true);

      setTimeout(() => {
        setShowTooltip(false);
      }, 3000);

      return;
    }

    setLoading(true);
    const phone = phoneNumber.replace(/\D/g, "");

    dispatch(
      preOrderIban({
        phoneNumber: phone,
        source: "питання по IBAN оплатах",
        onSuccess: () => {
          setIsModalOpen(true);
          setLoading(false);
          analytics().sendConversionEvent(FbPixelAnalyticsEvents.Contact, {
            content_name: "питання по IBAN оплатах",
          });
          analytics().sendEvent(
            `${AnalyticsEvents.iban_oplata_contact_us}_footer`
          );
        },
        onError: () => {
          setLoading(false);
        },
      })
    );
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setPhoneNumber("");
  };

  return (
    <>
      <Box sx={styles.block1}>
        <Container>
          <Typography variant="h2" color="grey000" sx={{ mb: 2 }}>
            Не знайшли відповіді на своє запитання?
          </Typography>
          <Typography variant="h5" color="grey000">
            Залишіть контактний номер і ми зв’яжемось з вами.
          </Typography>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="center"
            mt={4}
          >
            <Box sx={styles.inputWrapper}>
              <Box sx={styles.imageWrapper}>
                <img src={UAFlag} />
              </Box>
              <Tooltip
                open={showTooltip}
                title="Будь ласка, введіть номер телефону"
                arrow
                disableInteractive
                slotProps={{
                  tooltip: { style: styles.tooltip },
                  arrow: { style: styles.arrow },
                  popper: {
                    modifiers: [
                      { name: "offset", options: { offset: [0, 4] } },
                    ],
                  },
                }}
              >
                <Box sx={{ overflow: "hidden" }}>
                  <InputMask
                    mask="+38 (099) 999-9999"
                    onChange={onChangePhone}
                    value={phoneNumber}
                    maskChar={null}
                  >
                    {(inputMaskProps) => (
                      <TextField
                        {...inputMaskProps}
                        sx={styles.input}
                        variant="standard"
                        placeholder="+38 (000) 000-0000"
                        slotProps={{ input: { disableUnderline: true } }}
                      />
                    )}
                  </InputMask>
                </Box>
              </Tooltip>

              {!isXs && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onClick}
                  sx={styles.contactBtn}
                  loading={loading}
                  disabled={loading}
                >
                  Зателефонуйте мені
                </Button>
              )}
            </Box>
            {isXs && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={onClick}
                sx={{ ...styles.contactBtn, ...{ marginTop: "8px" } }}
                loading={loading}
              >
                Зателефонуйте мені
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="grey000" sx={{ mt: 1, mb: 2 }}>
            Продовжуючи, я погоджуюсь з умовами{" "}
            <Link
              component={RouterLink}
              variant="body2"
              to={routes.termsOfService}
              color="grey000"
            >
              Політики конфіденційності
            </Link>
          </Typography>
        </Container>
      </Box>
      <CallModal
        isOpen={isModalOpen}
        closeModal={onCloseModal}
        phone={phoneNumber}
      />
    </>
  );
};

const styles = {
  block1: {
    backgroundImage: `url(${ContactUsBg})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    paddingTop: { xs: "56px", sm: "96px" },
    paddingBottom: { xs: "56px", sm: "96px" },
    textAlign: "center",
  },
  inputWrapper: {
    backgroundColor: grey000,
    display: "flex",
    justifyContent: { xs: "start", sm: "center" },
    alignItems: "center",
    borderRadius: "28px",
    padding: "4px",
  },
  input: {
    marginLeft: "12px",
    minWidth: "240px",
    "& .MuiInputBase-input": {
      fontFamily: "Inter, sans-serif",
      fontSize: 16,
      fontWeight: "400",
      lineHeight: "24px",
      letterSpacing: "2px",
      minWidth: "240px",
    },
  },
  imageWrapper: {
    display: "flex",
    alignItems: "center",
    margin: "8px 0 8px 15px",
    paddingRight: "12px",
    borderRight: "1px solid #A6A7AD",
  },
  contactBtn: {
    padding: "12px 20px",
  },
  tooltip: {
    backgroundColor: grey000,
    color: grey900,
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    lineHeight: "130%",
    letterSpacing: "0",
    fontWeight: "500",
    borderRadius: "8px",
    padding: "8px 12px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.4)",
  },
  arrow: {
    color: grey000,
  },
};

export default ContactUs;
